import GitlabFeatureFlagsDataProvider from './GitlabFeatureFlagsDataProvider';
import UserManager from './UserManager';
import { store } from '../store/store';
import {
	gitlabFeatureFlagsFetchFailed,
	gitlabFeatureFlagsFetched,
	gitlabFeatureFlagsFetchStarted,
	resetGitlabFeatureFlags
} from '../store/gitlabFeatureFlagsSlice';

export const GITLAB_FEATURE_FLAG_POLL_INTERVAL_MS = 30 * 1000;

const hasCurrentAuthToken = authState => {
	const currentAuthToken = UserManager.authToken;
	return Boolean(
		authState?.isAuthorized &&
		authState.authToken &&
		currentAuthToken &&
		authState.authToken === currentAuthToken
	);
};

const getSessionKey = authState => {
	if (!hasCurrentAuthToken(authState)) return null;

	// Do not retain the token in the key. The user ID plus impersonation state is
	// enough to invalidate an old response when the evaluated user changes.
	return `${authState.user?.id || 'authenticated'}:${Boolean(authState.isImpersonating)}`;
};

const normalizeResponse = response => {
	if (!response || response.flags === null || typeof response.flags !== 'object' || Array.isArray(response.flags)) {
		throw new Error('Feature flag response did not contain a flags object');
	}

	const flags = Object.keys(response.flags).reduce((validFlags, key) => {
		if (typeof response.flags[key] === 'boolean') validFlags[key] = response.flags[key];
		return validFlags;
	}, {});

	return {
		flags,
		evaluatedAt: typeof response.evaluated_at === 'string' ? response.evaluated_at : null
	};
};

const normalizeError = error => ({
	status: error?.responseStatus || null,
	message: error?.json?.detail || error?.json?.message || error?.textStatus || error?.message || 'Feature flags unavailable'
});

class GitlabFeatureFlagPollingService {
	timer = null;
	inFlight = false;
	sessionKey = null;
	generation = 0;
	visibilityListenerAttached = false;

	syncWithAuth = authState => {
		const nextSessionKey = getSessionKey(authState);
		if (!nextSessionKey) {
			if (this.sessionKey) this.stop();
			return;
		}

		if (nextSessionKey !== this.sessionKey) this.start(nextSessionKey);
	};

	start = sessionKey => {
		this.stop(false);
		this.sessionKey = sessionKey;
		this.generation += 1;
		store.dispatch(resetGitlabFeatureFlags());
		this.attachVisibilityListener();
		this.poll(this.generation);
	};

	stop = (reset = true) => {
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
		// Invalidate the old request generation. Its eventual completion must not
		// block a new user's initial request.
		this.inFlight = false;
		this.sessionKey = null;
		this.generation += 1;
		this.detachVisibilityListener();
		if (reset) store.dispatch(resetGitlabFeatureFlags());
	};

	poll = generation => {
		if (!this.isCurrent(generation) || this.inFlight || document.hidden) return;
		if (!hasCurrentAuthToken(store.getState().auth)) {
			this.stop();
			return;
		}

		this.inFlight = true;
		store.dispatch(gitlabFeatureFlagsFetchStarted());

		GitlabFeatureFlagsDataProvider.getFeatureFlags()
			.then(response => {
				if (!this.isCurrent(generation)) return;
				const { flags, evaluatedAt } = normalizeResponse(response);
				store.dispatch(gitlabFeatureFlagsFetched({
					flags,
					evaluatedAt,
					fetchedAt: new Date().toISOString()
				}));
			})
			.catch(error => {
				if (this.isCurrent(generation)) store.dispatch(gitlabFeatureFlagsFetchFailed(normalizeError(error)));
			})
			.finally(() => {
				if (!this.isCurrent(generation)) return;
				this.inFlight = false;
				if (!document.hidden) {
					this.timer = setTimeout(() => this.poll(generation), GITLAB_FEATURE_FLAG_POLL_INTERVAL_MS);
				}
			});
	};

	isCurrent = generation => generation === this.generation && Boolean(this.sessionKey);

	attachVisibilityListener = () => {
		if (this.visibilityListenerAttached) return;
		document.addEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilityListenerAttached = true;
	};

	detachVisibilityListener = () => {
		if (!this.visibilityListenerAttached) return;
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilityListenerAttached = false;
	};

	handleVisibilityChange = () => {
		if (document.hidden) {
			if (this.timer) clearTimeout(this.timer);
			this.timer = null;
		} else if (this.sessionKey && !this.inFlight) {
			this.poll(this.generation);
		}
	};
}

export default new GitlabFeatureFlagPollingService();
